import { MessageCircle } from "lucide-react"

import { formatDate } from "@/lib/utils"
import { groupCommentsByParent } from "@/lib/comments"
import type { Comment } from "@/types/comment"

import { RecipeCommentForm } from "./recipe-comment-form"

type RecipeCommentsProps = {
  recipeId: string
  initialComments: Comment[]
}

function getCommentWord(count: number) {
  const mod10 = count % 10
  const mod100 = count % 100

  if (mod10 === 1 && mod100 !== 11) {
    return "коментар"
  }

  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) {
    return "коментарі"
  }

  return "коментарів"
}

export function RecipeComments({
  recipeId,
  initialComments,
}: RecipeCommentsProps) {
  const groupedComments = groupCommentsByParent(initialComments)
  const count = initialComments.length

  return (
    <section id="comments" className="space-y-6">
      <div className="rounded-[2rem] border border-neutral-200 bg-white p-6 shadow-sm sm:p-8">
        <div className="mb-6">
          <div className="eyebrow">Коментарі та питання</div>

          <h2 className="mt-4 text-2xl font-semibold text-neutral-950 sm:text-3xl">
            Обговорення рецепта
          </h2>

          <p className="mt-3 text-sm leading-7 text-neutral-600 sm:text-base">
            У публічному обговоренні відображаються лише коментарі, які вже
            пройшли модерацію. Тут можна поділитися уточненням, досвідом або
            поставити запитання.
          </p>
        </div>

        <div className="mb-6 rounded-[1.5rem] border border-neutral-200 bg-neutral-50 p-5">
          <div className="flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-emerald-100 text-emerald-800">
              <MessageCircle className="h-5 w-5" />
            </div>

            <div>
              <div className="text-lg font-semibold text-neutral-950">
                {count}
              </div>
              <div className="text-sm text-neutral-600">
                {count} {getCommentWord(count)} у публічному списку
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {groupedComments.length > 0 ? (
            groupedComments.map(({ root, replies }) => (
              <article
                key={root.id}
                className="rounded-[1.5rem] border border-neutral-200 bg-white p-5"
              >
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-neutral-950">
                      {root.authorName}
                    </h3>
                    <div className="mt-1 text-sm text-neutral-500">
                      {formatDate(root.createdAt)}
                    </div>
                  </div>
                </div>

                <p className="mt-4 text-sm leading-7 text-neutral-700 sm:text-base">
                  {root.text}
                </p>

                {replies.length > 0 ? (
                  <div className="mt-5 space-y-3 border-l-2 border-neutral-100 pl-4">
                    {replies.map((reply) => (
                      <div
                        key={reply.id}
                        className="rounded-[1.25rem] border border-neutral-200 bg-neutral-50 p-4"
                      >
                        <div className="text-sm font-semibold text-neutral-950">
                          {reply.authorName}
                        </div>
                        <div className="mt-1 text-xs text-neutral-500">
                          {formatDate(reply.createdAt)}
                        </div>
                        <p className="mt-3 text-sm leading-7 text-neutral-700">
                          {reply.text}
                        </p>
                      </div>
                    ))}
                  </div>
                ) : null}
              </article>
            ))
          ) : (
            <div className="rounded-[1.5rem] border border-dashed border-neutral-300 bg-neutral-50 p-6 text-sm leading-7 text-neutral-600">
              Поки що немає опублікованих коментарів. Ви можете почати
              обговорення нижче.
            </div>
          )}
        </div>
      </div>

      <RecipeCommentForm recipeId={recipeId} />
    </section>
  )
}